import * as STD from '@emersya/mpa-std';
import * as CM from '@emersya/mpa-components-manager';
import * as BasketNS from '@emersya/mpa-basket';
import { NS as ApplicationNS } from '@emersya/mpa-engine/src/app/client/components/Application/Application';
import { NS as OMSPriceNS } from '@emersya/mpa-engine/src/app/client/components/oms/Price/OMSPrice';
import ViewerComponent from '@emersya/mpa-engine/src/app/client/components/viewer/Viewer/Viewer';
import { NS as PlannerFurnitureCategoryTileNS } from '@emersya/mpa-engine/src/app/client/components/viewer/planner/furniture/CategoryTile/PlannerFurnitureCategoryTile';

export interface ITotalExpoPlannerFurnitureTileComponentConfiguration extends CM.DOMComponent.IConfiguration {
	readonly objectConfiguration: ApplicationNS.Configuration['PMS']['objects'][0];
};

export class TotalExpoPlannerFurnitureTileComponent extends CM.DOMComponent<
	HTMLDivElement,
	TotalExpoPlannerFurnitureTileComponent,
	ITotalExpoPlannerFurnitureTileComponentConfiguration
> {
	static override initialize() {
		super.initialize();
	};

	readonly plannerProduct!: ViewerComponent.Product;

	get category() {
		return <PlannerFurnitureCategoryTileNS.Component>this.component_parent?.component_parent;
	};

	component_DOM: HTMLDivElement;

	private async onMouseDown(ev: MouseEvent) {
		if (!app.viewer.planner) {
			this.component_throwUserError('', {});
		}
		if (this.component_DOM.classList.contains('limitReached') || app.viewer.planner.isDragging) {
			ev.preventDefault();
			return;
		}
		await app.viewer.planner.dragStart(ev, this.plannerProduct);
	};

	override async component_initialize() {
		await super.component_initialize();

		const currentProduct = app.currentProduct;
		if(!currentProduct){
			return;
		}
		const PC = this.component_configuration.objectConfiguration;
		if (!PC.SKU || !PC.d3.projectCode) {
			this.component_throwUserError('', {});
		}
		if (!app.viewer.planner) {
			this.component_throwUserError('', {});
		}
		const extension: Partial<ViewerComponent.IProductExtension> = {
			quantity: null,
			...(app.viewer.productsExtensionById[PC.SKU] || {})
		};
		app.viewer.productsExtensionById[PC.SKU] = <ViewerComponent.IProductExtension>extension;
		const addToExtension = (data: Partial<ViewerComponent.IProductExtension>) => {
			Object.assign(extension, data);
		};
		if (PC.pricing) {
			// const priceComponent = <Text.Component> this.component_find('@:bottom.left.price');
			// const price = PC.price[ app.currentCurrency.id ];
			// if (!price) {
			// 	this.component_throwUserError('Missing furniture price for current currency!');
			// }
			// const minimalPrice = typeof price === 'number' ? price : STD.misc.getDataInFunctionOfMinimalPositiveValue(price).value;
			// priceComponent.update(minimalPrice.toString() + app.currentCurrency.symbol);
			if (!app.viewer.planner) {
				this.component_throwUserError('', {});
			}
			addToExtension({
				pricing: BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>PC.pricing)
			});
			if (!extension.pricing) {
				this.component_throwUserError('', {});
			}
			const price = (extension.pricing instanceof BasketNS.Pricing.BoundPricing) ?
				// One day this code will boom 100% sure!
				await BasketNS.Pricing.BoundPricing.resolve(extension.pricing, <any>null) :
				await extension.pricing.resolve()
			;
			if (!price.exclTax) {
				this.component_throwLibraryError('', {});
			}
			const priceComponent = this.getPriceComponent();
			priceComponent.update(price.exclTax);
		}
		const currentConfiguration = PC.configurations.find(x => x.context.forProducts.includes(currentProduct.id));
		const PO = currentConfiguration && currentConfiguration.context.options || [];
		if (currentConfiguration && PO.length) {
			const presets: ViewerComponent.IProductPreset[] = [];
			for (const option of PO.filter(x => x.context.forProducts.includes(currentProduct.id))) {
				if (!option.translations['@name']) {
					this.component_throwUserError('Option without name!', {option});
				}
				presets.push({
					id: option.SKU,
					name: option.translations['@name'],
					iconURL: option.thumbnail.value,
					option: {
						type: 'preset',
						name: option.SKU
					},
					pricingInfo: !option.pricingInfo ? null : {
						pricing: BasketNS.Pricing.deserialize(<BasketNS.Pricing.FinalPricing.ISerialized & BasketNS.Pricing.BoundPricing.ISerialized>option.pricingInfo.pricing),
						volumeComputationMethod: option.pricingInfo.volumeComputationMethod
					}
				});
			};
			const defaultInFunctionOfGlobalProduct = currentConfiguration.context.defaultOption[currentProduct.id];
			if(!defaultInFunctionOfGlobalProduct){
				this.component_throwUserError('Missing default option for this furniture' + PC.SKU + 'and this product', {});
			};
			const self = this;
			addToExtension({
				presets,
				defaultPreset: defaultInFunctionOfGlobalProduct,
				resolveSpecificKey(
					this: ViewerComponent.ProductInstance | ViewerComponent.Product,
					what: 'pricing' | 'quantity' | 'customImagesPricing' | 'defaultPreset'
				) {
					if (what === 'defaultPreset') {
						if (!app.virtualCurrentProductSKU) {
							<never>self.component_throwLibraryError('Not implemented!', {});
						}
						return (app.virtualCurrentProductSKU);
					}
					<never>self.component_throwLibraryError('Not implemented!', {});
					return '';
				}
			});
		}
		const nameTr = PC.translations['@name'];
		if (!nameTr) {
			this.component_throwUserError(`Product component<${PC.SKU}> does not have @name translation!`, {});
		}
		(<any>this).plannerProduct = this.category.plannerCategory.createProduct({
			SKU: PC.SKU,
			previewPath: PC.iconURL || '',
			name: nameTr[app.currentLanguage],
			positionsSetId: null,
			nodes: [
				{
					SKU: PC.SKU,
					projectCode: PC.d3.projectCode,
					localId: 'global',
					parentLocalId: 'root',
					snappable: false,
					matrix: app.viewer.mtx([0, 0, 0], [1, 1, 1], 0),
					tracked: true,
					draggable: true
				}
			]
		});
	};

	protected getPriceComponent() {
		return <OMSPriceNS.Component>this.component_find('@:left.price');
	}

	constructor(
		descriptor: ConstructorParameters<typeof CM.Component>[0],
		configuration: ITotalExpoPlannerFurnitureTileComponentConfiguration
	) {
		STD.object.deepMergeAssign(configuration, [
			{
				'@innerConfigurations': {
					left: {
						'@innerConfigurations': {
							name: {
								'@translations': {
									text: configuration.objectConfiguration.translations['@name'],
									'title-attr': configuration.objectConfiguration.translations['@name']
								}
							}
						}
					},
					right: {
							'@innerConfigurations': {
								thumbnail: {
									URL: configuration.objectConfiguration.iconURL,
									'@translations': {
										'@name' : {
											'en' : configuration.objectConfiguration.SKU,
											'fr' : configuration.objectConfiguration.SKU
										}
									}
								}
							}
						}
					}
			}
		]);
		super(descriptor, configuration);
		this.component_DOM = <HTMLDivElement>STD.DOM.factory({
			tag: 'div',
			events: {
				mousedown: this.onMouseDown.bind(this)
			},
		});
		this.component_DOM.classList.add('component-viewer-planner-furniture-productTile');
	};
}

export namespace NS {
	export const Component = TotalExpoPlannerFurnitureTileComponent;
	export type Component = TotalExpoPlannerFurnitureTileComponent;
	export type Configuration = ITotalExpoPlannerFurnitureTileComponentConfiguration;
}
import manifestXMLSource from './PlannerFurnitureProductTile.xml';
import './PlannerFurnitureProductTile.scss';

CM.Manager.declare({
	id: 'totalExpo-viewer-planner-furniture-productTile',
	type: TotalExpoPlannerFurnitureTileComponent,
	manifest: manifestXMLSource
});
